import React, { useState, useRef, useEffect } from "react";
import { Box, TextField } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import Typography from "../../../components/Typography/Typography";
import { editArticleTitle } from "../../../services/common.service";
import { getToast } from "../../../common/utils";
import { useAppSelector } from "../../../hooks/store";

interface EditableTitleProps {
  title: string;
  onTitleUpdate: (newTitle: string) => void;
  className?: string;
}

const EditableTitle: React.FC<EditableTitleProps> = ({
  title,
  onTitleUpdate,
  className,
}) => {
  const session_id = useAppSelector(
    (store) => store?.rootReducer?.commonReducer?.session_id
  );
  const [isEditing, setIsEditing] = useState(false);
  const [editedTitle, setEditedTitle] = useState(title);
  const [isHovered, setIsHovered] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Update local state when title prop changes
  useEffect(() => {
    setEditedTitle(title);
  }, [title]);

  // Focus input when editing starts
  useEffect(() => {
    if (isEditing && inputRef.current) {
      // Use setTimeout to ensure the input is fully rendered
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          // Check if select method exists before calling it
          if (typeof inputRef.current.select === "function") {
            inputRef.current.select();
          } else {
            // Alternative approach: set selection range
            inputRef.current.setSelectionRange(
              0,
              inputRef.current.value.length
            );
          }
        }
      }, 0);
    }
  }, [isEditing]);

  const handleTitleClick = () => {
    setIsEditing(true);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEditedTitle(event.target.value);
  };

  const handleKeyDown = async (
    event: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (event.key === "Enter") {
      await handleSave();
    } else if (event.key === "Escape") {
      handleCancel();
    }
  };

  const handleInputBlur = async () => {
    await handleSave();
  };

  const handleSave = async () => {
    if (!editedTitle.trim()) {
      setEditedTitle(title); // Reset to original if empty
      setIsEditing(false);
      return;
    }

    if (editedTitle.trim() === title.trim()) {
      setIsEditing(false);
      return;
    }

    setIsUpdating(true);

    try {
      const response = await editArticleTitle(session_id, {
        title: editedTitle.trim(),
      });

      if (response.status) {
        onTitleUpdate(response.title || editedTitle.trim());
        getToast("Success", "Title updated successfully");
      } else {
        getToast("Error", response.status_message || "Failed to update title");
        setEditedTitle(title); // Reset on failure
      }
    } catch (error) {
      getToast("Error", "Failed to update title");
      setEditedTitle(title); // Reset on error
    } finally {
      setIsUpdating(false);
      setIsEditing(false);
    }
  };

  const handleCancel = () => {
    setEditedTitle(title);
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <TextField
        inputRef={inputRef}
        value={editedTitle}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onBlur={handleInputBlur}
        disabled={isUpdating}
        variant="standard"
        sx={{
          "& .MuiInput-root": {
            fontSize: "14px",
            fontWeight: 700,
            color: "#282B31",
            "&:before": {
              borderBottomColor: "#E5E7EB",
            },
            "&:hover:not(.Mui-disabled):before": {
              borderBottomColor: "#3474E0",
            },
            "&.Mui-focused:after": {
              borderBottomColor: "#3474E0",
            },
          },
          "& .MuiInput-input": {
            padding: "2px 0",
          },
        }}
        inputProps={{
          style: {
            fontSize: "14px",
            fontWeight: 700,
          },
        }}
      />
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        cursor: "pointer",
        position: "relative",
        "&:hover .edit-icon": {
          opacity: 1,
        },
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handleTitleClick}
      className={className}
    >
      <Typography
        color="#282B31"
        fontWeight={400}
        fontSize={14}
        sx={{
          display: "flex",
          alignItems: "center",
          "&:hover": {
            color: "#3474E0",
          },
        }}
      >
        <b>{title}</b>
      </Typography>
      <EditIcon
        className="edit-icon"
        sx={{
          fontSize: "16px",
          color: "#6B7280",
          marginLeft: "6px",
          opacity: 0,
          transition: "opacity 0.2s ease-in-out",
          "&:hover": {
            color: "#3474E0",
          },
        }}
      />
    </Box>
  );
};

export default EditableTitle;
